'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useRef, Suspense } from 'react';

function AnalyticsTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isInitialRender = useRef(true);

  useEffect(() => {
    // 1. Skip the first render because @next/third-parties/google <GoogleAnalytics /> 
    // already fires a page_view on the initial hard load.
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }

    if (!pathname) return;

    // 2. Wait a tick for Next.js to update document.title
    const timeoutId = setTimeout(() => {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        const url = searchParams?.toString() 
          ? `${pathname}?${searchParams.toString()}` 
          : pathname;

        // 3. Send the config event which officially updates the page state in GA4,
        // using the active tracking ID from the environment or data layer.
        // If gtag was loaded by @next/third-parties, it manages the ID, but we can pass it here or trigger an update.
        // When ID is dynamic, 'event' 'page_view' is safer if you don't know the exact ID, 
        // but 'config' with the specific ID is better. 
        // A robust fallback is to just push the event 'page_view' with accurate page_location & page_title.
        window.gtag('event', 'page_view', {
          page_path: url,
          page_location: window.location.href,
          page_title: document.title,
        });
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [pathname, searchParams]);

  return null;
}

export default function NavigationTracker() {
  return (
    <Suspense fallback={null}>
      <AnalyticsTracker />
    </Suspense>
  );
}
